import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Card, Dropdown, Button, Popconfirm, Divider, Empty, Spin } from 'antd';
import { EllipsisOutlined, WarningOutlined } from '@ant-design/icons';
import './Memberships.css';

const { Title, Text } = Typography;

const FormatDate = ({ date }) => {
    if (!date) return 'N/A';
    try {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    } catch (e) {
        return date;
    }
};

const MembershipCard = ({ membershipCard, patientBasicInfo, loading, handleVisits, handleMenuClick }) => {
    const items = [
        { key: 'view', label: 'View Details' },
        { key: 'history', label: 'View History' },
        { key: 'terms', label: 'Terms & Conditions' }
    ];

    // Logic to determine color variation
    let colorClass = 'color-teal';
    if (!membershipCard.status) {
        colorClass = 'color-gray';
    } else if (membershipCard.plans?.toLowerCase().includes('gold')) {
        colorClass = 'color-purple';
    } else if (membershipCard.plans?.toLowerCase().includes('plus')) {
        colorClass = 'color-green';
    }

    return (
        <Card
            hoverable
            className={`membership-card ${membershipCard?.status ? 'active-card' : 'inactive-card'} ${colorClass}`}
            loading={loading}
        >
            {/* Card Header */}
            <div className="card-header">
                <div>
                    <div className="card-title">Digital</div>
                    <div className="card-subtitle">Membership Card</div>
                </div>
                <div className="card-actions">
                    {/* Status Badge */}
                    <div className={`status-badge ${membershipCard?.status ? 'active' : 'inactive'}`}>
                        {membershipCard?.status ? 'Active' : 'Inactive'}
                    </div>


                </div>
            </div>

            {/* Card Content */}
            <div className="card-content">
                {/* Member Info */}
                <div className="member-info">
                    <div className="member-details">
                        <div className="member-name">
                            {patientBasicInfo?.first_name || membershipCard?.name?.split(' ')[0]} {patientBasicInfo?.last_name || membershipCard?.name?.split(' ').slice(1).join(' ')}
                        </div>
                        <div className="member-type">Member</div>
                    </div>
                    <div className="visits-display">
                        <div className="visits-number-large">{membershipCard?.visits}</div>
                        <div className="visits-label">Visits</div>
                    </div>
                </div>

                {/* Membership Details */}
                <div className="membership-details">
                    <div className="detail-row">
                        <span className="detail-label">Plan:</span>
                        <span className="detail-value">{membershipCard?.plans}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">MID:</span>
                        <span className="detail-value">
                            {membershipCard?.MID_number}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Start:</span>
                        <span className="detail-value">
                            {membershipCard?.start_date && (
                                <FormatDate date={membershipCard?.start_date} />
                            )}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">End:</span>
                        <span className="detail-value">
                            {membershipCard?.expiration_date && (
                                <FormatDate date={membershipCard?.expiration_date} />
                            )}
                        </span>
                    </div>
                </div>
            </div>
        </Card>
    );
};

const Memberships = () => {
    const [activeMemberships, setActiveMemberships] = useState([]);
    const [historicalMemberships, setHistoricalMemberships] = useState([]);
    const [patientBasicInfo, setPatientBasicInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const userDataString = sessionStorage.getItem('userData');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                setPatientBasicInfo(userData?.patient_info);

                let allMemberships = [];
                if (userData.memberships && Array.isArray(userData.memberships)) {
                    allMemberships = userData.memberships;
                } else if (userData.patient_memberships && Array.isArray(userData.patient_memberships)) {
                    allMemberships = userData.patient_memberships;
                }

                // Split into active and historical
                const active = allMemberships.filter(m => m.status === true);
                const historical = allMemberships.filter(m => m.status === false);

                setActiveMemberships(active);
                setHistoricalMemberships(historical);
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }
        setLoading(false);
    }, []);

    const handleVisits = (membership) => {
        console.log("Claiming visit for:", membership);
        // In a real app, you'd call an API here.
    };

    const handleMenuClick = (key, membership) => {
        console.log(`Action: ${key} for membership:`, membership);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="memberships-page p-4 md:p-6">
            <div className="mb-8">
                <Title level={4}>My Memberships</Title>

            </div>

            <div className="membership-container">
                <Title level={5} className="mb-4">Active Memberships</Title>
                {activeMemberships.length > 0 ? (
                    <Row gutter={[24, 24]}>
                        {activeMemberships.map((membership) => (
                            <Col xs={24} sm={24} md={12} lg={12} xl={8} key={membership.id}>
                                <MembershipCard
                                    membershipCard={membership}
                                    patientBasicInfo={patientBasicInfo}
                                    loading={false}
                                    handleVisits={handleVisits}
                                    handleMenuClick={handleMenuClick}
                                />
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Empty description="No active memberships found" />
                )}
            </div>

            <Divider className="section-divider" />

            <div className="membership-container">
                <Title level={5} className="history-title">Historical Memberships</Title>
                {historicalMemberships.length > 0 ? (
                    <Row gutter={[24, 24]}>
                        {historicalMemberships.map((membership) => (
                            <Col xs={24} sm={24} md={12} lg={12} xl={8} key={membership.id}>
                                <MembershipCard
                                    membershipCard={membership}
                                    patientBasicInfo={patientBasicInfo}
                                    loading={false}
                                    handleMenuClick={handleMenuClick}
                                />
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Empty description="No historical memberships found" />
                )}
            </div>
        </div>
    );
};

export default Memberships;
